import { useRef, useState } from 'react'
import type { MouseEvent } from 'react'

import { IconButton } from '@twistopayments/design-system/components/atoms/button'
import { Typography } from '@twistopayments/design-system/components/atoms/typography'
import { ArrowLeftIcon } from '@twistopayments/design-system/icons/homepage'

import type { SectionsCarouselComponent } from '@twisto/api-cms/generated-types'

import { useHighlightedText } from 'homepage/libs/hooks'

import { CarouselItem } from './carousel-item'
import * as styles from './carousel.styles'

export const Carousel = ({
  title,
  highlighted,
  carouselItems,
}: SectionsCarouselComponent) => {
  const { before, highlight, after } = useHighlightedText(title, highlighted)
  const slider = useRef<HTMLDivElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeftPosition, setScrollLeftPosition] = useState(0)

  const onMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (!slider.current) {
      return
    }
    setIsDragging(true)
    setStartX(e.pageX - slider.current.offsetLeft)
    setScrollLeftPosition(slider.current.scrollLeft)
  }

  const onMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !slider.current) {
      return
    }

    e.preventDefault()

    const pageX = e.pageX

    const x = pageX - slider.current.offsetLeft
    const dist = x - startX
    slider.current.scrollLeft = scrollLeftPosition - dist
  }

  const onMouseUpLeave = () => {
    setIsDragging(false)
  }

  const handleButtonClick = (direction: -1 | 1) => {
    if (!slider.current) {
      return
    }

    slider.current.scrollBy({
      left: direction * slider.current.scrollWidth,
      behavior: 'smooth',
    })
  }

  const scrollLeft = () => {
    handleButtonClick(-1)
  }

  const scrollRight = () => {
    handleButtonClick(1)
  }

  return (
    <section css={styles.section}>
      <Typography css={styles.title} fontWeight="heavy" variant="h2">
        {before}
        <span css={styles.highlighted}>{highlight}</span>
        {after}
      </Typography>

      {carouselItems && (
        <div
          ref={slider}
          css={styles.container}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseUpLeave}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUpLeave}
        >
          {carouselItems.map((item, index) => (
            <CarouselItem key={index} {...item} />
          ))}
        </div>
      )}

      <div css={styles.controls}>
        <IconButton
          color="ghost"
          css={styles.controlButton}
          icon={ArrowLeftIcon}
          onClick={scrollLeft}
        />
        <IconButton
          color="ghost"
          css={[styles.controlButton, styles.controlButtonNext]}
          icon={ArrowLeftIcon}
          onClick={scrollRight}
        />
      </div>
    </section>
  )
}
